import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { STATS } from "../data/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNvcnBvcmF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODQ2NTY4NDB8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Your Outsourced", "Legal", "Department"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.15 },
  }),
};

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Abstract architecture" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#050A14]/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050A14] via-transparent to-[#050A14]/60" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 lg:px-12 pb-20 pt-40">
        <h1 className="text-[13vw] md:text-[9vw] lg:text-[8.5vw] font-900 leading-[0.9] tracking-tightest text-[#F2EFE9]">
          {LINES.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                {line === "Legal" ? <span className="text-[#D95A41]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between"
        >
          <p className="max-w-xl text-lg md:text-xl text-[#94A3B8] leading-relaxed">
            A law firm that works two ways: your embedded Fractional General Counsel for board-level
            judgment day-to-day — and a private practice for the discrete matters when you simply need sharp, experienced legal advice.
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => navigate("/home/book")}
              data-testid="hero-book-btn"
              className="px-7 py-4 rounded-full bg-[#D95A41] text-[#F2EFE9] font-700 hover:bg-[#E6735A] transition-colors duration-300"
            >
              Book a free consultation
            </button>
            <button
              onClick={() => navigate("/home/practice")}
              data-testid="hero-explore-btn"
              className="px-7 py-4 rounded-full border border-white/20 text-[#F2EFE9] font-500 hover:bg-white/5 transition-colors duration-300"
            >
              Our practice
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#050A14] p-6" data-testid={`stat-${s.value}`}>
              <div className="text-3xl md:text-4xl font-900 text-[#F2EFE9]">{s.value}</div>
              <div className="text-xs text-[#94A3B8] mt-2 leading-snug">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-12 z-10 flex items-center gap-2 text-[#94A3B8] text-xs tracking-widest uppercase"
      >
        Scroll <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
